import React, {useCallback} from 'react';

import {isSupplementAnchor} from './annotationAnchors';
import {announceAnnotationChange} from './AnnotationAnnouncer';
import {focusAnnotationFallbackIfLost} from './annotationFocusFallback';
import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {
  selectAnnotationAnchor,
  selectAnnotationNote,
  selectAnnotationType,
  selectHighlightColor
} from './annotationSelectors';
import type {AnnotationAnchor} from './annotationTypes';

interface AnnotationEditorProps {
  regionId: string;
  /** The mark being edited and the viewport rect its popover hangs from. */
  activeAnnotation: AnnotationAnchor;
  /** A live box to hang from instead of that rect. */
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  /** False where a mark cannot be an underline (a whole-supplement border). */
  allowUnderline?: boolean;
  /** Opened from a note's pencil: focus starts in the note field. */
  focusNote?: boolean;
}

/**
 * Store-connected editor for one active mark: reads the mark's style and note
 * by (regionId, id) and applies edits through the store actions. Renders
 * nothing once the mark is gone.
 */
export default function AnnotationEditor({
  regionId,
  activeAnnotation,
  anchorEl,
  onClose,
  allowUnderline = true,
  focusNote = false
}: AnnotationEditorProps) {
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const setAnnotationNote = useAnnotationModeStore((state) => state.setAnnotationNote);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const annotationId = activeAnnotation.id;
  const color = useAnnotationModeStore((state) =>
    selectHighlightColor(state, regionId, annotationId)
  );
  const type = useAnnotationModeStore((state) =>
    selectAnnotationType(state, regionId, annotationId)
  );
  const note = useAnnotationModeStore((state) =>
    selectAnnotationNote(state, regionId, annotationId)
  );
  const anchor = useAnnotationModeStore((state) =>
    selectAnnotationAnchor(state, regionId, annotationId)
  );
  const handleNoteChange = useCallback(
    (nextNote: string) => {
      if (nextNote.trim()) {
        setAnnotationNote(regionId, annotationId, nextNote);
        return;
      }
      const savedNote = selectAnnotationNote(
        useAnnotationModeStore.getState(),
        regionId,
        annotationId
      );
      if (savedNote === undefined) {
        return;
      }
      const focused = document.activeElement;
      setAnnotationNote(regionId, annotationId, '');
      // Deferred past the commit: the focused control may go with the note (a margin indicator
      // does), and a focus move after the announcement would cut it off.
      setTimeout(() => {
        if (focused && !focused.isConnected) {
          focusAnnotationFallbackIfLost();
        }
        announceAnnotationChange('Note deleted.');
      });
    },
    [regionId, annotationId, setAnnotationNote]
  );

  if (!color || !type || !anchor) {
    return null;
  }

  return (
    // Keyed so switching marks always remounts with that mark's own note draft.
    <AnnotationPopover
      key={annotationId}
      anchorRect={activeAnnotation.rect}
      anchorEl={anchorEl}
      selectedColor={color}
      selectedType={type}
      note={note ?? ''}
      noteLabel={
        isSupplementAnchor(anchor) ? 'Note on this supplement' : `Note on “${anchor.exact}”`
      }
      focusNote={focusNote}
      allowUnderline={allowUnderline}
      onSelectColor={(nextColor) => setHighlightColor(regionId, annotationId, nextColor)}
      onSelectUnderline={() => setAnnotationUnderline(regionId, annotationId)}
      onNoteChange={handleNoteChange}
      onRemove={() => {
        removeAnnotation(regionId, annotationId);
        onClose();
      }}
      onDismiss={onClose}
    />
  );
}
